import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NavMenuComponent } from './nav-menu/nav-menu.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavMenuComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'INT.ItemRegistry.Web.AdminTool';
  isIframe = false;
  loginDisplay = false;
  msalInitialized = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public authService: MsalService,
    private msalBroadcastService: MsalBroadcastService, private cdr: ChangeDetectorRef
  ) { }

    async ngOnInit(): Promise<void> {
        // Force change detection after authentication check
        setTimeout(() => {
        this.cdr.detectChanges();
        }, 0);

        this.isIframe = window !== window.parent && !window.opener;
        
        // Wait for MSAL to initialize before proceeding
        try {
            await this.authService.instance.initialize();
            this.msalInitialized = true;
            this.setLoginDisplay();

            this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

            /**
             * You can subscribe to MSAL events as shown below. For more info,
             * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
             */

            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
                )
                .subscribe((result: EventMessage) => {
                    if (this.authService.instance.getAllAccounts().length === 0) {
                        window.location.pathname = "/";
                    } else {
                        this.setLoginDisplay();
                    }
                });

            // Set active account immediately upon successful login/token acquisition
            // so that acquireTokenSilent calls don't race against inProgress$ === None
            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS ||
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                    ),
                    takeUntil(this._destroying$)
                )
                .subscribe((result: EventMessage) => {
                    const payload = result.payload as AuthenticationResult;
                    if (payload?.account) {
                        this.authService.instance.setActiveAccount(payload.account);
                    }
                    this.setLoginDisplay();
                });

            this.msalBroadcastService.inProgress$
                .pipe(
                    filter((status: InteractionStatus) => status === InteractionStatus.None),
                    takeUntil(this._destroying$)
                )
                .subscribe(() => {
                    this.setLoginDisplay();
                    this.checkAndSetActiveAccount();
                });
        } catch (error) {
            console.error('MSAL initialization failed:', error);
        }
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            let accounts = this.authService.instance.getAllAccounts();
            // add your code for handling multiple accounts here
            this.authService.instance.setActiveAccount(accounts[0]);
        }

        // If no active account, trigger login
        if (!this.authService.instance.getActiveAccount()) {
        this.login();
        }
    }

    login() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            } else {
                this.authService.loginPopup()
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
            } else {
                this.authService.loginRedirect();
            }
        }
    }

    logout() {
        this.authService.logout();
    }

    // unsubscribe to events when component is destroyed
    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}